import React from "react";
import { css } from "@emotion/react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { AGENT_TASK_STATE } from "constants/index";
import { ActionButton } from "components/Buttons";
import { Spinner } from "components/Loader";
import { FormFieldError } from "components/Form";
import { Col, Grid, Row } from "components/Containers";
import Icon, { ICON_SIZE, ICON_TYPE } from "components/Icon";
import { InlineProgressBar } from "components/Progress";
import { colors, fontSize, margins } from "style";
import { PrimaryLink } from "components/Links";

/**
 * ProfileResumeAutoFillProcessing
 *
 * @param {Object}      task
 * @param {Function}    setShowUploader
 * @param {Number}      progress
 * @param {Boolean}     disabled
 * @param {String}      disabledMessageId
 * @param {Boolean}     cancelling
 * @param {Function}    handleCancelProfileCreationAgentTask
 */
const ProfileResumeAutoFillProcessing = ({
  task,
  setShowUploader,
  progress,
  disabled,
  handleCancelProfileCreationAgentTask,
  cancelling,
  disabledMessageId = "Profile.ProfileResumeAutoFill.DisabledMessage",
}) => {
  const resumeUrl = task?.resumeUrl;
  const stepsCompleted = task?.progress?.stepsCompleted;
  const stepsTotal = task?.progress?.stepsTotal;

  const isProcessing = task?.state === AGENT_TASK_STATE.processing;
  const isCompleted = task?.state === AGENT_TASK_STATE.completed;
  const isError = task?.state === AGENT_TASK_STATE.failed;
  const isCanceled = task?.state === AGENT_TASK_STATE.canceled;

  const handleUseDifferentResume = () => {
    if (disabled) return;

    setShowUploader(true);
  };

  const handleCancel = () => {
    handleCancelProfileCreationAgentTask(task?.externalTaskId);
  };

  return (
    <Grid cols={6} gap={margins.xsmall} rowGap={margins.xsmall} css={styles.processing_container}>
      <Grid.col start={1} end={isProcessing ? 3 : 4}>
        {resumeUrl && (
          <Row css={styles.file}>
            <Icon type={ICON_TYPE.file} size={ICON_SIZE.xlarge} color={colors.purpleRainBase} />

            <Col css={styles.details} gap={margins.xxxsmall}>
              <span css={styles.label}>
                <FormattedMessage id="Profile.ProfileResumeAutoFill.Resume" />
              </span>

              <PrimaryLink href={resumeUrl} target="_blank" rel="noopener noreferrer" css={styles.resume_link}>
                <FormattedMessage id="Profile.ProfileResumeAutoFill.View" />
              </PrimaryLink>
            </Col>

            {!isProcessing && !disabled && (
              <ActionButton type="button" onClick={handleUseDifferentResume}>
                <FormattedMessage id="Profile.ProfileResumeAutoFill.UseDifferentResume" />
              </ActionButton>
            )}
          </Row>
        )}
      </Grid.col>

      <Grid.col start={isProcessing ? 3 : 4} end={7}>
        {isProcessing && (
          <Col gap={margins.xxsmall}>
            <Row css={styles.processing_status}>
              <Spinner size="2.6rem" margin="0" />

              <Col css={styles.details} gap={margins.xxxsmall}>
                <span css={styles.label}>
                  {task?.agentStatus || <FormattedMessage id="Profile.ProfileResumeAutoFill.Analyzing" />}
                </span>

                <span css={styles.metadata}>
                  <FormattedMessage id="Profile.ProfileResumeAutoFill.ProcessingDescription" />
                </span>
              </Col>

              {progress !== null && <InlineProgressBar completed={stepsCompleted} total={stepsTotal} />}

              <ActionButton type="button" onClick={handleCancel} disabled={cancelling}>
                <FormattedMessage
                  id={`Profile.ProfileResumeAutoFill.${cancelling ? "Cancelling" : "CancelProcessing"}`}
                />
              </ActionButton>
            </Row>

            {progress !== null && (
              <div css={styles.progress_track}>
                <div css={styles.progress_bar(progress)} />
              </div>
            )}
          </Col>
        )}

        {isCompleted && (
          <Col gap={margins.xxsmall}>
            <Row css={styles.success}>
              <Icon type={ICON_TYPE.checkmark} size={ICON_SIZE.medium} color="inherit" />

              <FormattedMessage id="Profile.ProfileResumeAutoFill.Success" />
            </Row>

            {disabled && (
              <span css={styles.disabled_message}>
                <FormattedMessage id={disabledMessageId} />
              </span>
            )}

            {!resumeUrl && !disabled && (
              <ActionButton type="button" onClick={handleUseDifferentResume}>
                <FormattedMessage id="Profile.ProfileResumeAutoFill.UseDifferentResume" />
              </ActionButton>
            )}
          </Col>
        )}

        {isError && (
          <Col gap={margins.xxsmall}>
            <FormFieldError width="100%">
              <FormattedMessage id="Profile.ProfileResumeAutoFill.Error" />
            </FormFieldError>

            {disabled && (
              <span css={styles.disabled_message}>
                <FormattedMessage id={disabledMessageId} />
              </span>
            )}

            {!resumeUrl && !disabled && (
              <ActionButton type="button" onClick={handleUseDifferentResume}>
                <FormattedMessage id="Profile.ProfileResumeAutoFill.TryDifferentResume" />
              </ActionButton>
            )}
          </Col>
        )}

        {isCanceled && (
          <Col gap={margins.xxsmall}>
            <FormFieldError width="100%">
              <FormattedMessage id="Profile.ProfileResumeAutoFill.Canceled" />
            </FormFieldError>

            {disabled && (
              <span css={styles.disabled_message}>
                <FormattedMessage id={disabledMessageId} />
              </span>
            )}
          </Col>
        )}
      </Grid.col>
    </Grid>
  );
};

const styles = {
  processing_container: css`
    align-items: center;
  `,

  processing_status: css`
    flex-wrap: wrap;
  `,

  file: css`
    min-width: 0;
    flex-wrap: wrap;
    padding: ${margins.xxsmall};
    border: 1px solid ${colors.grayAnatomyLight4};
    border-radius: 0.8rem;
  `,

  details: css`
    min-width: 0;
    flex: 1;
  `,

  label: css`
    color: ${colors.purpleRainDark2};
    font-size: ${fontSize.xsmall};
    font-weight: 600;
  `,

  metadata: css`
    color: ${colors.grayAnatomyLight1};
    font-size: ${fontSize.xxsmall};
    line-height: 1.4;
  `,

  resume_link: css`
    color: ${colors.purpleRainBase};
    font-size: ${fontSize.xxsmall};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  `,

  progress_track: css`
    overflow: hidden;
    height: 0.6rem;
    border-radius: 2.4rem;
    background: ${colors.grayAnatomyLight4};
  `,

  progress_bar: (progress) => css`
    width: ${progress}%;
    height: 100%;
    border-radius: inherit;
    background: ${colors.purpleRainBase};
    transition: width 0.3s ease;
  `,

  success: css`
    flex-wrap: wrap;
    color: ${colors.green};
    font-size: ${fontSize.xsmall};
    font-weight: 500;
  `,

  disabled_message: css`
    color: ${colors.grayAnatomyBase};
    font-size: ${fontSize.xxsmall};
    line-height: 1.4;
  `,
};

ProfileResumeAutoFillProcessing.propTypes = {
  task: PropTypes.object.isRequired,
  setShowUploader: PropTypes.func.isRequired,
  progress: PropTypes.number,
  disabled: PropTypes.bool,
  disabledMessageId: PropTypes.string,
  cancelling: PropTypes.bool,
  handleCancelProfileCreationAgentTask: PropTypes.func.isRequired,
};

export default ProfileResumeAutoFillProcessing;
